import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import WorkspaceAssistant from './WorkspaceAssistant';

const PortfolioProposalContext = createContext({ refinement:null, onRefinement:()=>{}, drafts:{}, rememberDraft:()=>{} });
export const usePortfolioProposal = () => useContext(PortfolioProposalContext);
const AssistantContext = createContext(null);
export const WorkspaceAssistantPageContext = createContext({});
// Bumped whenever a stored suggestion is applied from the assistant or from a
// recommendation panel, so each re-reads /performance/runs and shows it applied.
const AppliedRecommendationsContext = createContext({ version: 0, bump: () => {} });
export const useAppliedRecommendations = () => useContext(AppliedRecommendationsContext);

// One mounted assistant owns the conversation. Move its portal container into
// the active modal: a body-level widget is inert behind showModal's top layer.
export function WorkspaceAssistantProvider({ accountId, userId, page, children, hidden = false }) {
  const [refinement,onRefinement] = useState(null);
  const [drafts,setDrafts] = useState({});
  const rememberDraft = useCallback((key, draft) => setDrafts(previous => JSON.stringify(previous[key]) === JSON.stringify(draft) ? previous : {...previous,[key]:draft}), []);
  useEffect(() => { setDrafts({});onRefinement(null); },[accountId,userId]);
  const [surfaces, setSurfaces] = useState({});
  const [host] = useState(() => document.createElement('div'));
  const register = useCallback((id, surface) => setSurfaces(current => ({ ...current, [id]: surface })), []);
  const remove = useCallback(id => setSurfaces(current => {
    const next = { ...current }; delete next[id]; return next;
  }), []);
  const api = useMemo(() => ({ register, remove }), [register, remove]);
  const [appliedVersion, setAppliedVersion] = useState(0);
  const bump = useCallback(() => setAppliedVersion(version => version + 1), []);
  const applied = useMemo(() => ({ version: appliedVersion, bump }), [appliedVersion, bump]);
  const ordered = Object.values(surfaces).filter(surface => surface.ref.current?.open)
    .sort((a, b) => a.ref.current.compareDocumentPosition(b.ref.current) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
  const active = ordered.at(-1);
  const activeHost = active?.ref.current || document.body;
  useLayoutEffect(() => {
    host.className = 'ow-assistant-host';
    host.hidden = hidden;
    activeHost.appendChild(host);
    return () => { host.remove(); };
  }, [host, activeHost, hidden]);
  return <PortfolioProposalContext.Provider value={{refinement,onRefinement,drafts,rememberDraft}}><AssistantContext.Provider value={api}><AppliedRecommendationsContext.Provider value={applied}>{children}{createPortal(
    <WorkspaceAssistant accountId={accountId} userId={userId} page={Object.assign({}, page, ...ordered.map(surface => surface.page))} persistent
      appliedVersion={appliedVersion} onApplied={bump} onRefinement={onRefinement} />,
    host,
  )}</AppliedRecommendationsContext.Provider></AssistantContext.Provider></PortfolioProposalContext.Provider>;
}

export function useWorkspaceAssistantSurface(ref, page) {
  const api = useContext(AssistantContext), id = useId();
  const serialized = JSON.stringify(page);
  useEffect(() => {
    if (!api) return;
    api.register(id, { ref, page: JSON.parse(serialized) });
  }, [api, id, ref, serialized]);
  useEffect(() => () => api?.remove(id), [api, id]);
}
